/**
 * Directus CMS API Integration
 * This module provides utility functions for interacting with the Directus CMS API
 */

// Authentication details
const API_BASE_URL = 'https://cms.fourtwenty.dev';
const AUTH_TOKEN = 'XsGOoe2uO0aUQugyg-_zBWGJjGPZdk8k';

// Default headers for all API requests
const headers = {
  'Authorization': `Bearer ${AUTH_TOKEN}`,
  'Content-Type': 'application/json'
};

// Cache configuration
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
const cache = {
  services: { data: null, timestamp: 0 },
  portfolio: { data: null, timestamp: 0 },
  testimonials: { data: null, timestamp: 0 }
};

/**
 * Check if cached data is still valid
 * @param {string} key - Cache key
 * @returns {boolean} - Whether cache is valid
 */
const isCacheValid = (key) => {
  const cacheEntry = cache[key];
  if (!cacheEntry.data) return false;
  
  const now = Date.now();
  return now - cacheEntry.timestamp < CACHE_DURATION;
};

/**
 * Update cache with new data
 * @param {string} key - Cache key
 * @param {any} data - Data to cache
 */
const updateCache = (key, data) => {
  cache[key] = {
    data,
    timestamp: Date.now()
  };
};

/**
 * Generic fetch function with error handling
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Fetch options
 * @returns {Promise<any>} - Response data
 */
const fetchFromAPI = async (endpoint, options = {}) => {
  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        ...headers,
        ...(options.headers || {})
      }
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API fetch error:', error);
    throw error;
  }
};

/**
 * Fetch all services from the CMS
 * @param {boolean} [forceRefresh=false] - Force a refresh from the API
 * @returns {Promise<Array>} - Services data
 */
export const fetchServices = async (forceRefresh = false) => {
  if (!forceRefresh && isCacheValid('services')) {
    return cache.services.data;
  }

  const result = await fetchFromAPI('/items/natux_services');
  updateCache('services', result);
  return result;
};

/**
 * Fetch a specific service by ID
 * @param {number|string} id - Service ID
 * @returns {Promise<Object>} - Service data
 */
export const fetchServiceById = async (id) => {
  return fetchFromAPI(`/items/natux_services/${id}`);
};

/**
 * Fetch all portfolio items from the CMS
 * @param {boolean} [forceRefresh=false] - Force a refresh from the API
 * @returns {Promise<Array>} - Portfolio data
 */
export const fetchPortfolio = async (forceRefresh = false) => {
  if (!forceRefresh && isCacheValid('portfolio')) {
    return cache.portfolio.data;
  }

  const result = await fetchFromAPI('/items/natux_portfolio');
  updateCache('portfolio', result);
  return result;
};

/**
 * Fetch a specific portfolio item by ID
 * @param {number|string} id - Portfolio item ID
 * @returns {Promise<Object>} - Portfolio item data
 */
export const fetchPortfolioItemById = async (id) => {
  return fetchFromAPI(`/items/natux_portfolio/${id}`);
};

/**
 * Fetch all testimonials from the CMS
 * @param {boolean} [forceRefresh=false] - Force a refresh from the API
 * @returns {Promise<Array>} - Testimonials data
 */
export const fetchTestimonials = async (forceRefresh = false) => {
  if (!forceRefresh && isCacheValid('testimonials')) {
    return cache.testimonials.data;
  }

  const result = await fetchFromAPI('/items/natux_testimonials');
  updateCache('testimonials', result);
  return result;
};

/**
 * Fetch a specific testimonial by ID
 * @param {number|string} id - Testimonial ID
 * @returns {Promise<Object>} - Testimonial data
 */
export const fetchTestimonialById = async (id) => {
  return fetchFromAPI(`/items/natux_testimonials/${id}`);
};

/**
 * Helper function to get the full image URL from an image ID
 * @param {string} imageId - Image ID from Directus
 * @returns {string} - Full image URL
 */
export const getImageUrl = (imageId) => {
  if (!imageId) return null;
  return `${API_BASE_URL}/assets/${imageId}`;
};

/**
 * Submit a new testimonial to the CMS
 * @param {Object} testimonialData - The testimonial data to submit
 * @returns {Promise<Object>} - Response data
 */
export const submitTestimonial = async (testimonialData) => {
  return fetchFromAPI('/items/natux_testimonials', {
    method: 'POST',
    body: JSON.stringify(testimonialData)
  });
};

export default {
  fetchServices,
  fetchServiceById,
  fetchPortfolio,
  fetchPortfolioItemById,
  fetchTestimonials,
  fetchTestimonialById,
  getImageUrl,
  submitTestimonial
};
